import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import {
  MessageDialog,
  type MessageDialogKind,
  type MessageDialogVariant,
} from '../components/common/MessageDialog';

export interface ConfirmDialogOptions {
  title?: string;
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** default 'danger' (red confirm button) — most confirms guard destructive actions */
  variant?: MessageDialogVariant;
}

export interface AlertDialogOptions {
  title?: string;
  message: ReactNode;
  okLabel?: string;
  /** default 'info'; use 'error' / 'success' / 'warning' for the matching icon */
  variant?: MessageDialogVariant;
}

export interface PromptDialogOptions {
  title?: string;
  message?: ReactNode;
  placeholder?: string;
  defaultValue?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  inputType?: string;
}

export interface DialogContextValue {
  /** In-app replacement for window.confirm — resolves true if confirmed */
  confirm: (options: ConfirmDialogOptions | string) => Promise<boolean>;
  /** In-app replacement for window.alert — resolves when dismissed */
  alert: (options: AlertDialogOptions | string) => Promise<void>;
  /** In-app replacement for window.prompt — resolves to the value, or null if cancelled */
  prompt: (options: PromptDialogOptions | string) => Promise<string | null>;
}

interface DialogRequest {
  kind: MessageDialogKind;
  variant?: MessageDialogVariant;
  title?: string;
  message?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  placeholder?: string;
  defaultValue?: string;
  inputType?: string;
  resolve: (value: string | boolean | null) => void;
}

const DialogContext = createContext<DialogContextValue | null>(null);

export function DialogProvider({ children }: { children: ReactNode }) {
  // Queue so overlapping requests display one after another instead of clobbering
  const [queue, setQueue] = useState<DialogRequest[]>([]);
  const active = queue[0] ?? null;

  const enqueue = useCallback((request: DialogRequest) => {
    setQueue((prev) => [...prev, request]);
  }, []);

  const settle = useCallback(
    (value: string | boolean | null) => {
      if (!active) return;
      active.resolve(value);
      setQueue((prev) => prev.slice(1));
    },
    [active],
  );

  const confirm = useCallback(
    (options: ConfirmDialogOptions | string) => {
      const opts = typeof options === 'string' ? { message: options } : options;
      return new Promise<boolean>((resolve) => {
        enqueue({
          kind: 'confirm',
          variant: opts.variant ?? 'danger',
          title: opts.title,
          message: opts.message,
          confirmLabel: opts.confirmLabel,
          cancelLabel: opts.cancelLabel,
          resolve: (v) => resolve(v === true),
        });
      });
    },
    [enqueue],
  );

  const alert = useCallback(
    (options: AlertDialogOptions | string) => {
      const opts = typeof options === 'string' ? { message: options } : options;
      return new Promise<void>((resolve) => {
        enqueue({
          kind: 'alert',
          variant: opts.variant ?? 'info',
          title: opts.title,
          message: opts.message,
          confirmLabel: opts.okLabel,
          resolve: () => resolve(),
        });
      });
    },
    [enqueue],
  );

  const prompt = useCallback(
    (options: PromptDialogOptions | string) => {
      const opts = typeof options === 'string' ? { message: options } : options;
      return new Promise<string | null>((resolve) => {
        enqueue({
          kind: 'prompt',
          title: opts.title,
          message: opts.message,
          placeholder: opts.placeholder,
          defaultValue: opts.defaultValue,
          confirmLabel: opts.confirmLabel,
          cancelLabel: opts.cancelLabel,
          inputType: opts.inputType,
          resolve: (v) => resolve(typeof v === 'string' ? v : null),
        });
      });
    },
    [enqueue],
  );

  const value = useMemo(() => ({ confirm, alert, prompt }), [confirm, alert, prompt]);

  return (
    <DialogContext.Provider value={value}>
      {children}
      {active && (
        <MessageDialog
          open
          kind={active.kind}
          variant={active.variant}
          title={active.title}
          message={active.message}
          confirmLabel={active.confirmLabel}
          cancelLabel={active.cancelLabel}
          placeholder={active.placeholder}
          defaultValue={active.defaultValue}
          inputType={active.inputType}
          onConfirm={(inputValue) => settle(active.kind === 'prompt' ? (inputValue ?? '') : true)}
          onCancel={() =>
            settle(active.kind === 'alert' ? true : active.kind === 'prompt' ? null : false)
          }
        />
      )}
    </DialogContext.Provider>
  );
}

export function useDialog(): DialogContextValue {
  const context = useContext(DialogContext);
  if (!context) {
    throw new Error('useDialog must be used within a DialogProvider');
  }
  return context;
}
